import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'What is 1MEDICINE?',
    answer:
      '1MEDICINE is a focused workspace for medical learning and reference. It brings structured modules, personal resources, search, content tools, and sharing into one organized library you can return to from the web or your phone.',
  },
  {
    question: 'Who is 1MEDICINE designed for?',
    answer:
      'It is built for healthcare professionals, trainees, and medical learners who want a calmer way to organize useful material. You can shape your library around a specialty, role, exam, course, or day-to-day learning priorities.',
  },
  {
    question: 'How can I personalize my workspace?',
    answer:
      'Choose the modules that matter to you, save useful resources, and organize content around your own workflow. Available personalization options depend on your plan and the modules you use.',
  },
  {
    question: 'Can I create and share my own resources?',
    answer:
      'Content tools let you create and refine resources in a consistent editor. When sharing is appropriate, you can make selected content available to an organization, team, or individual using the available visibility controls.',
  },
  {
    question: 'Can I use 1MEDICINE on mobile?',
    answer:
      'Yes. 1MEDICINE is available on the web, iPhone and Android. The mobile experience is designed for reviewing resources and returning to your library when you are away from a larger screen.',
  },
  {
    question: 'What is included in the Free and Pro plans?',
    answer:
      'The Free plan provides a practical way to explore available modules and core workspace tools. 1MED Pro expands access with premium modules, advanced discovery and personalization, content creation, collaboration options, and priority support. Current availability is shown in the app.',
  },
  {
    question: 'Does 1MEDICINE replace clinical judgment?',
    answer:
      'No. 1MEDICINE supports learning and information organization. It is not medical advice and is not a substitute for professional judgment, validated clinical guidance, independent verification, or your organization’s policies and approved sources.',
  },
  {
    question: 'Where can I get help or ask another question?',
    answer:
      'Visit the Contact page to reach the 1MEDICINE team. For questions about billing, availability, privacy, or a specific feature, include the email associated with your account and a short description so the team can help efficiently.',
  },
];

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  return (
    <section id="faq" className="section-shell scroll-mt-20 border-y border-slate-200/80 bg-white">
      <div className="site-container">
        <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16">
          <div className="max-w-md">
            <p className="section-kicker">FAQ</p>
            <h2 className="section-title">Everything you need to get oriented.</h2>
            <p className="section-copy mt-4">
              Learn how the workspace, mobile apps, plans, and sharing tools fit together.
            </p>
            <Link to="/contact" className="secondary-button mt-7">
              Ask us a question
              <span aria-hidden="true">→</span>
            </Link>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              const answerId = `faq-answer-${index}`;
              const buttonId = `faq-button-${index}`;

              return (
                <article key={faq.question} className="surface-card overflow-hidden">
                  <h3>
                    <button
                      id={buttonId}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={answerId}
                      onClick={() => toggleFAQ(index)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-slate-900 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-emerald-600 sm:px-6"
                    >
                      <span>{faq.question}</span>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                        <svg
                          aria-hidden="true"
                          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                  </h3>
                  {isOpen && (
                    <div
                      id={answerId}
                      role="region"
                      aria-labelledby={buttonId}
                      className="border-t border-slate-100 px-5 py-4 sm:px-6"
                    >
                      <p className="text-sm leading-6 text-slate-600">{faq.answer}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
